import { Outlet, useNavigate, Link } from "react-router-dom";
import { useTheme } from "../App";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";
import { Sun, Moon, LogOut, User, Calendar, FileText, Copy, Building2, Settings } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { RotalieLogo } from "./RotalieLogo";
import { toast } from "sonner";

// Copy to clipboard helper
const copyToClipboard = async (text, label = "Copied!") => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch (err) {
    toast.error("Failed to copy");
  }
};

const StaffLayout = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <RotalieLogo showText={false} />
              <div>
                <h1 className="text-xl font-bold tracking-tight font-['Inter']" data-testid="staff-app-title">Rotalie</h1>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  {company?.name || "My Schedule"}
                </p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {/* My Rota - Main and only navigation item */}
              <Link
                to="/staff"
                data-testid="nav-my-rota"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground"
              >
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">My Rota</span>
              </Link>
              
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="ml-2"
                data-testid="theme-toggle"
                aria-label="Toggle theme"
              >
                {theme === "light" ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )}
              </Button>

              {/* Settings Icon */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/staff/settings")}
                data-testid="staff-settings-btn"
                aria-label="Settings"
              >
                <Settings className="w-5 h-5" />
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="ml-1" data-testid="staff-user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center border border-border">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{user?.name || "Staff Member"}</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        {user?.email || "Staff"}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/staff/account" className="flex items-center" data-testid="my-account-link">
                      <User className="w-4 h-4 mr-2" />
                      My Account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {company && (
                    <>
                      <DropdownMenuItem 
                        className="cursor-pointer"
                        onClick={() => copyToClipboard(company.company_code, "Company Code copied!")}
                      >
                        <Building2 className="w-4 h-4 mr-2" />
                        <span className="text-xs flex-1">
                          {company.name}
                          <code className="bg-muted px-1 ml-1 rounded">{company.company_code}</code>
                        </span>
                        <Copy className="w-3 h-3 ml-2 text-muted-foreground" />
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/documentation" className="flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Help
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
};

export default StaffLayout;
